import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowRight, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { useLanguagePrefix } from '@/hooks/useLanguagePrefix';

const NotFoundPage = () => {
  const { t } = useTranslation();
  const langPrefix = useLanguagePrefix();
  
  useEffect(() => {
    // Log 404 for analytics
    if (window.gtag) {
      window.gtag('event', 'page_not_found', {
        page_path: window.location.pathname
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-kitco-navy via-grey-900 to-grey-900 flex items-center justify-center px-4">
      <SEOHead 
        title={t('notFound.title')}
        description="The page you're looking for doesn't exist. Return to Kitco Digital Metals homepage to explore our institutional precious metals platform."
        canonical={`${langPrefix}${window.location.pathname}`}
      />
      
      <div className="max-w-2xl w-full text-center">
        {/* 404 Visual */}
        <div className="mb-8">
          <h1 className="text-9xl font-bold text-kitco-gold mb-4 animate-pulse">404</h1>
          <div className="flex justify-center mb-6">
            <Search className="h-20 w-20 text-grey-500" />
          </div>
        </div>

        {/* Message */}
        <h2 className="text-4xl font-bold text-white mb-4">{t('notFound.heading')}</h2>
        <p className="text-xl text-grey-300 mb-8">
          {t('notFound.message')}
        </p>

        {/* Helpful Info */}
        <div className="bg-grey-700/50 border border-gray-700 rounded-lg p-6 mb-8">
          <p className="text-grey-300">
            {t('notFound.legacy')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={`${langPrefix}/`}>
            <Button 
              size="lg" 
              className="bg-kitco-gold hover:bg-kitco-gold-hover text-black font-semibold w-full sm:w-auto"
            >
              <Home className="mr-2 h-5 w-5" />
              {t('notFound.home')}
            </Button>
          </Link>
          
          <Link to={`${langPrefix}/direct-reserve`}>
            <Button 
              size="lg" 
              variant="outline"
              className="border-kitco-gold text-kitco-gold hover:bg-kitco-gold hover:text-black w-full sm:w-auto"
            >
              {t('notFound.directReserve')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>

        {/* Quick Links */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <p className="text-grey-400 text-sm mb-4">Quick Links:</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to={`${langPrefix}/`} className="text-kitco-gold hover:text-amber-300">Home</Link>
            <span className="text-grey-600">•</span>
            <Link to={`${langPrefix}/direct-reserve`} className="text-kitco-gold hover:text-amber-300">DirectReserve™</Link>
            <span className="text-grey-600">•</span>
            <Link to={`${langPrefix}/contact`} className="text-kitco-gold hover:text-amber-300">{t('notFound.contact')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
